<template>
  <!-- The day arc (design 2026-10-26): the five parts of the day as one
       control. Night at the ends, noon at the top; the sky behind it changes
       colour and a sun glides to the pick. A radiogroup, arrow keys move. -->
  <div class="bm-arc" :class="{ unknown: timeKind === 'unknown' }" data-testid="bm-arc">
    <svg viewBox="0 -10 360 200" role="radiogroup" :aria-labelledby="labelledby">
      <defs>
        <radialGradient :id="uid + 'sky'" cx="50%" cy="100%" r="80%">
          <stop offset="0" :stop-color="skyColour" />
          <stop offset="1" stop-color="#0d0520" stop-opacity="0" />
        </radialGradient>
        <radialGradient :id="uid + 'sun'">
          <stop offset="0" stop-color="#fff3c9" />
          <stop offset=".5" stop-color="#f0d9a3" />
          <stop offset="1" stop-color="#f0d9a3" stop-opacity="0" />
        </radialGradient>
      </defs>
      <path class="arc-sky" :d="skyPath" :fill="`url(#${uid}sky)`" />
      <g v-for="(s, i) in segs" :key="s.key" ref="segEls" class="seg-hit"
         :class="{ on: s.key === onKey && timeKind === 'window', near: s.key === onKey && timeKind === 'exact' }"
         role="radio" :tabindex="tabFor(i)" :aria-label="`${s.label}, ${s.hint}`"
         :aria-checked="String(s.key === onKey && timeKind === 'window')" :data-testid="'bm-arc-' + s.key"
         @click="$emit('pick', s.key)" @keydown="onSegKey($event, i)">
        <path class="seg-band" :d="s.band" />
        <text class="seg-ico" :x="s.ix" :y="s.iy">{{ s.ico }}</text>
        <text class="seg-lbl" :x="s.lx" :y="s.ly" :text-anchor="s.anchor">{{ s.short }}</text>
      </g>
      <line x1="20" :y1="CY" x2="340" :y2="CY" stroke="rgba(240,217,163,.35)" />
      <text class="arc-tick" :x="CX - R" :y="CY + 18">MIDNIGHT</text>
      <text class="arc-tick" :x="CX" :y="CY - R + 44">NOON</text>
      <text class="arc-tick" :x="CX + R" :y="CY + 18">MIDNIGHT</text>
      <g v-if="sunAt != null" class="sun-mark" :transform="sunTransform" data-testid="bm-arc-sun">
        <circle r="22" :fill="`url(#${uid}sun)`" opacity=".7" />
        <circle r="9" fill="#f6dfa6" stroke="#fff6dc" stroke-width="1.5" />
      </g>
    </svg>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { WINDOWS } from '@/utils/birth-moment'
import { WINDOW_SHORT, windowOfClock } from '@/utils/birth-ask'

const props = defineProps({
  timeKind: { type: String, default: '' },
  windowKey: { type: String, default: '' },
  exactTime: { type: String, default: '' },
  reduced: { type: Boolean, default: false },
  labelledby: { type: String, default: '' },
})
const emit = defineEmits(['pick'])

const uid = `bmarc${Math.random().toString(36).slice(2, 8)}`
const CX = 180; const CY = 168; const R = 124
const RANGES = { early_morning: [0, 7], morning: [7, 12], afternoon: [12, 17], evening: [17, 21], late_evening: [21, 24] }
const ICONS = { early_morning: '☾', morning: '☀', afternoon: '☀', evening: '☾', late_evening: '✧' }
const SKY = { early_morning: '#1d1a4a', morning: '#6a3f6e', afternoon: '#7a5a3a', evening: '#6b2f4d', late_evening: '#16123d' }
const ang = (h) => Math.PI * (1 - h / 24)
const pt = (h, r = R) => [CX + r * Math.cos(ang(h)), CY - r * Math.sin(ang(h))]
function arcPath (a, b, r = R) {
  const [x1, y1] = pt(a, r); const [x2, y2] = pt(b, r)
  return `M${x1.toFixed(1)} ${y1.toFixed(1)} A${r} ${r} 0 0 1 ${x2.toFixed(1)} ${y2.toFixed(1)}`
}
const skyPath = `M${CX - R + 16} ${CY} A${R - 16} ${R - 16} 0 0 1 ${CX + R - 16} ${CY} Z`

const segs = WINDOWS.map((w) => {
  const [from, to] = RANGES[w.key]
  const mid = (from + to) / 2
  const [lx, ly] = pt(mid, R + 30); const [ix, iy] = pt(mid)
  return {
    key: w.key, label: w.label, hint: w.hint, short: WINDOW_SHORT[w.key], ico: ICONS[w.key] + '︎',
    band: arcPath(from + 0.12, to - 0.12), lx: lx.toFixed(1), ly: (ly + 4).toFixed(1), ix: ix.toFixed(1), iy: iy.toFixed(1),
    anchor: lx < CX - 40 ? 'end' : lx > CX + 40 ? 'start' : 'middle', mid,
  }
})

const target = computed(() => {
  if (props.timeKind === 'window') { const s = segs.find((x) => x.key === props.windowKey); return s ? s.mid : null }
  if (props.timeKind === 'exact') {
    const m = /^(\d{1,2}):(\d{2})$/.exec(props.exactTime || '')
    return m ? Number(m[1]) + Number(m[2]) / 60 : null
  }
  return null
})
const onKey = computed(() => {
  if (props.timeKind === 'window') return props.windowKey
  if (props.timeKind === 'exact') { const w = windowOfClock(props.exactTime); return w ? w.key : '' }
  return ''
})
const skyColour = computed(() => SKY[onKey.value] || '#2a1845')

// The sun glides (650ms, ease-out) unless motion is reduced.
const sunAt = ref(target.value)
let raf = 0
watch(target, (to) => {
  cancelAnimationFrame(raf)
  if (to == null) { sunAt.value = null; return }
  const from = sunAt.value
  if (props.reduced || from == null || typeof requestAnimationFrame !== 'function') { sunAt.value = to; return }
  const t0 = performance.now(); const dur = 650
  const step = (now) => {
    const k = Math.min(1, (now - t0) / dur); const e = 1 - Math.pow(1 - k, 3)
    sunAt.value = from + (to - from) * e
    if (k < 1) raf = requestAnimationFrame(step)
  }
  raf = requestAnimationFrame(step)
})
onBeforeUnmount(() => cancelAnimationFrame(raf))
const sunTransform = computed(() => { const [x, y] = pt(sunAt.value); return `translate(${x.toFixed(1)} ${y.toFixed(1)})` })

// Roving tabindex: the checked part, else the first, takes the tab stop.
function tabFor (i) {
  const idx = segs.findIndex((s) => s.key === onKey.value)
  return (idx === -1 ? i === 0 : i === idx) ? 0 : -1
}
const segEls = ref([])
function onSegKey (e, i) {
  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); emit('pick', segs[i].key); return }
  if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(e.key)) return
  e.preventDefault()
  const next = (i + (/Right|Down/.test(e.key) ? 1 : segs.length - 1)) % segs.length
  emit('pick', segs[next].key)
  const el = segEls.value[next]
  if (el && el.focus) el.focus()
}
</script>

<style scoped>
.bm-arc { position: relative; max-width: 360px; margin: 0 auto; }
.bm-arc svg { display: block; width: 100%; height: auto; overflow: visible; }
.seg-hit { cursor: pointer; outline: none; }
.seg-hit:focus-visible .seg-band { stroke: rgba(240, 217, 163, 0.75); }
.seg-band { fill: none; stroke: rgba(195, 169, 255, 0.18); stroke-width: 30; transition: stroke 0.3s; }
.seg-hit:hover .seg-band { stroke: rgba(240, 217, 163, 0.3); }
.seg-hit.on .seg-band { stroke: rgba(240, 217, 163, 0.62); }
.seg-hit.near .seg-band { stroke: rgba(240, 217, 163, 0.3); }
.seg-ico { font: 15px 'Apple Symbols', 'Segoe UI Symbol', 'Noto Sans Symbols 2', 'DejaVu Sans', sans-serif; fill: #e9dcff;
  pointer-events: none; text-anchor: middle; dominant-baseline: central; }
.seg-lbl { font: 600 15px 'Letter Serif', 'Cormorant Garamond', Georgia, serif; fill: #bfaee0; pointer-events: none; }
.seg-hit.on .seg-lbl { fill: #f0d9a3; }
.arc-tick { font: 600 10px 'Letter Display', 'Cinzel', Georgia, serif; letter-spacing: 0.1em; fill: #7d6b9c; text-anchor: middle; }
.arc-sky { transition: opacity 0.6s; }
.bm-arc.unknown .seg-band { stroke: rgba(195, 169, 255, 0.08); }
.bm-arc.unknown .seg-lbl { fill: #6f5f8e; }
@media (prefers-reduced-motion: reduce) {
  .seg-band, .arc-sky { transition: none; }
}
</style>
